import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Offcanvas } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import CustomInputWithLabel from "src/layouts/form/CustomInputWithLabel";
import { useAppDispatch } from "src/store/hooks";
import {
  InsuranceProviderData,
  InsuranceProviderDataForm,
  InsuranceProviderEditDataForm,
  addInsuranceProvider,
  refreshInsuranceProviderList,
  updateInsuranceProvider,
} from "src/store/reducers/insuranceProviders";
import {
  EmailRequired,
  KeyRequired,
  StringLengthValidation,
} from "src/layouts/form/validations/Validation";
import { FormikAPIErrorHandler } from "src/utils/errorUtil";
import CustomTextAreaWithLabel from "src/layouts/form/CustomTextAreaWithLabel";

type IInsuranceProviderFormCanvas = {
  Title: string;
  show: boolean;
  editId: string;
  hide: () => void;
  providerData?: InsuranceProviderData;
};

const InsuranceProviderFormCanvas = ({
  Title,
  show,
  hide,
  editId,
  providerData,
}: IInsuranceProviderFormCanvas) => {
  const [pending, setPending] = useState(false);
  const dispatch = useAppDispatch();

  const {
    handleChange,
    setErrors,
    values,
    handleSubmit,
    touched,
    errors,
    resetForm,
  } = useFormik({
    initialValues: {
      provider_name: providerData?.provider_name || "",
      provider_code: providerData?.provider_code || "",
      contact_person_name: providerData?.contact_person_name || "",
      contact_number: providerData?.contact_number || "",
      email_id: providerData?.email_id || "",
      notes: providerData?.notes || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      provider_name: StringLengthValidation("Provider name", 2, 50),
      provider_code: StringLengthValidation("Provider code", 2, 20),
      contact_person_name: StringLengthValidation("Contact person", 3, 30),
      contact_number: KeyRequired("Contact number"),
      email_id: EmailRequired("Email address", false),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      if (editId !== "Add") {
        setPending(true);
        editU({ ...values, id: editId });
      } else {
        setPending(true);
        addU(values);
      }
    },
  });

  const handleHide = useCallback(() => {
    hide();
  }, [hide]);

  const addU = useCallback(
    async (values: InsuranceProviderDataForm) => {
      try {
        await dispatch(addInsuranceProvider(values)).unwrap();
        handleHide();
        toast.success("Insurance provider is added successfully");
      } catch (error: any) {
        FormikAPIErrorHandler(error.message, setErrors, toast.error);
      } finally {
        setPending(false);
      }
    },
    [dispatch, handleHide, setErrors]
  );

  const editU = useCallback(
    async (values: InsuranceProviderEditDataForm) => {
      try {
        await dispatch(updateInsuranceProvider(values)).unwrap();
        toast.success("Insurance provider is updated successfully");
        dispatch(refreshInsuranceProviderList());
        handleHide();
      } catch (error: any) {
        FormikAPIErrorHandler(error.message, setErrors, toast.error);
      } finally {
        setPending(false);
      }
    },
    [dispatch, handleHide, setErrors]
  );

  useEffect(() => {
    resetForm();
  }, [resetForm, show]);

  return (
    <Offcanvas
      show={show}
      onHide={handleHide}
      className="offcanvas-end customeoff"
      placement="end"
    >
      <div className="offcanvas-header">
        <h5 className="modal-title" id="#gridSystemModal">
          {Title}
        </h5>
        <button type="button" className="btn-close" onClick={handleHide}>
          <i className="fa-solid fa-xmark"></i>
        </button>
      </div>
      <div className="offcanvas-body">
        <div className="container-fluid">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="provider_name"
                  labelText="Provider Name"
                  required={true}
                  handleChange={handleChange}
                  value={values.provider_name}
                  touched={touched.provider_name}
                  errors={errors.provider_name}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="provider_code"
                  labelText="Provider Code"
                  required={true}
                  handleChange={handleChange}
                  value={values.provider_code}
                  touched={touched.provider_code}
                  errors={errors.provider_code}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="contact_person_name"
                  labelText="Contact Person Name"
                  handleChange={handleChange}
                  value={values.contact_person_name}
                  touched={touched.contact_person_name}
                  errors={errors.contact_person_name}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="contact_number"
                  labelText="Contact Number"
                  required={true}
                  handleChange={handleChange}
                  value={values.contact_number}
                  touched={touched.contact_number}
                  errors={errors.contact_number}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="email_id"
                  inputType="email"
                  labelText="Email"
                  handleChange={handleChange}
                  value={values.email_id}
                  touched={touched.email_id}
                  errors={errors.email_id}
                />
              </div>
              <div className="col-xl-12 mb-3">
                <CustomTextAreaWithLabel
                  id="notes"
                  labelText="Notes"
                  handleChange={handleChange}
                  value={values.notes}
                  touched={touched.notes}
                  errors={errors.notes}
                />
              </div>
              <div>
                <button
                  type="submit"
                  disabled={pending}
                  className="btn btn-primary me-1"
                >
                  Submit
                </button>
                <Link
                  to={"#"}
                  onClick={handleHide}
                  className="btn btn-danger light ms-1"
                >
                  Cancel
                </Link>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Offcanvas>
  );
};

export default InsuranceProviderFormCanvas;
